import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Shield, ChevronRight } from 'lucide-react';
import SEO from '../components/SEO';

export const Terms = () => {
  const { t } = useTranslation();

  const sections = Array.from({ length: 10 }, (_, i) => i + 1);

  return (
    <div className="relative min-h-screen pt-24 pb-16 px-4 bg-[#03050b] text-slate-200 selection:bg-emerald-500/30">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-1/2 translate-x-1/2 w-[1000px] h-[600px] bg-emerald-500/5 blur-[120px] rounded-full" />
      </div>
      
      <div className="relative max-w-5xl mx-auto z-10">
        <SEO
          title={`${t('navigation.legal.terms') || 'Terms'} – Victor Lazari`}
          description={t('terms.title') as string}
          canonical={"https://victorlazari.com/terms"}
          image={"https://raw.githubusercontent.com/VictorLazari-lab/victorlazari.com/VictorLazari-Media/varienda%20minimalist%20logo.png"}
          type="website"
        />

        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-white/10 bg-white/5 text-emerald-400">
            <Shield className="w-4 h-4" />
            <span className="text-xs font-mono uppercase tracking-[0.3em]">Service Agreement</span>
          </div>
          <h1 className="mt-8 text-4xl md:text-6xl font-black text-white tracking-tight leading-none">
            {t('terms.title')}
          </h1>
        </motion.div>

        <div className="space-y-8">
          {sections.map((section) => {
            const items = t(`terms.section${section}.items`, { returnObjects: true, defaultValue: [] });

            return (
              <motion.section
                key={section}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                className="rounded-3xl border border-white/10 bg-white/[0.02] px-6 py-8 md:px-10 md:py-10 hover:bg-white/[0.04] transition-colors duration-500"
              >
                <h2 className="text-xl font-bold text-white mb-6 leading-tight flex items-center gap-3">
                  <span className="text-emerald-500/50 text-sm font-mono">0{section}</span>
                  {t(`terms.section${section}.title`)}
                </h2>

                <p className="text-base text-slate-400 leading-relaxed mb-6 font-light">
                  {t(`terms.section${section}.content`)}
                </p>

                {Array.isArray(items) && items.length > 0 && (
                  <ul className="space-y-3 text-sm text-slate-400">
                    {(items as string[]).map((item: string, index: number) => (
                      <li key={index} className="flex items-start gap-3 leading-relaxed group">
                        <ChevronRight className="w-4 h-4 text-emerald-500/50 mt-1 flex-shrink-0 group-hover:text-emerald-400 transition-colors" />
                        <span className="group-hover:text-slate-300 transition-colors">{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </motion.section>
            );
          })}

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-xs text-slate-600 text-center mt-16 font-mono uppercase tracking-widest"
          >
            {t('terms.lastUpdated')} // VERSION_HASH: 8f3a2c
          </motion.p>
        </div>
      </div>
    </div>
  );
};